import { useState, useEffect } from "react";
import { SuperAdminLayout, CVUploadModal } from "@/components/admin";
import { subscribeToAdminReferralsWithUI, AdminReferralWithUI } from "@/api/referrals";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, ExternalLink } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export default function CVMissing() {
  const [referrals, setReferrals] = useState<AdminReferralWithUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReferral, setSelectedReferral] = useState<AdminReferralWithUI | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const setupSubscription = async () => {
      try {
        setLoading(true);

        // Subscribe to referrals that require resume upload
        unsubscribe = subscribeToAdminReferralsWithUI((allReferrals) => {
          // Filter for referrals that require resume upload
          const filtered = allReferrals.filter(r => r.requiresResumeUpload === true);
          setReferrals(filtered);
          setLoading(false);
        });
      } catch (error) {
        console.error("Error setting up CV missing subscription:", error);
        setLoading(false);
      }
    };

    setupSubscription();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const handleUploadClick = (referral: AdminReferralWithUI) => {
    setSelectedReferral(referral);
    setShowUploadModal(true);
  };

  const handleUploadSuccess = () => {
    // Refresh will happen automatically via subscription
    setShowUploadModal(false);
    setSelectedReferral(null);
  };

  if (loading) {
    return (
      <SuperAdminLayout
        title="CV Missing"
        subtitle="Manage referrals with missing CVs"
      >
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </SuperAdminLayout>
    );
  }

  if (referrals.length === 0) {
    return (
      <SuperAdminLayout
        title="CV Missing"
        subtitle="Manage referrals with missing CVs"
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-2">
            <p className="text-gray-500 text-lg">No referrals requiring CV upload</p>
            <p className="text-gray-400 text-sm">All referrals have their CVs uploaded</p>
          </div>
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout
      title="CV Missing"
      subtitle="Manage referrals with missing CVs"
    >
      <Card>
        <CardHeader>
          <CardTitle className="space-grotesk">Referrals Missing CVs</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                    Candidate Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                    Job
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                    LinkedIn
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {referrals.map((referral) => (
                  <tr key={referral.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-slate-900 space-grotesk">
                        {referral.candidateName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900 space-grotesk">
                        {referral.jobTitle}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {referral.candidateData?.linkedinUrl ? (
                        <a
                          href={referral.candidateData.linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline space-grotesk"
                        >
                          View Profile
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-sm text-slate-400 space-grotesk">Not provided</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <Button
                        variant="secondary"
                        size="default"
                        onClick={() => handleUploadClick(referral)}
                        className="space-grotesk"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload CV
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <CVUploadModal
        open={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setSelectedReferral(null);
        }}
        referral={selectedReferral}
        onUploadSuccess={handleUploadSuccess}
      />
    </SuperAdminLayout>
  );
}
